import axios from 'axios';
import type {
  ChatRequest, ChatResponse,
  ZoneCalcRequest, ZoneCalcResponse,
  FTPTestRequest, FTPTestResponse,
  TrainingPlanRequest, TrainingPlanResponse,
  RecoveryRequest, RecoveryResponse,
  RacePrepRequest, RacePrepResponse,
  WorkoutLogRequest, WorkoutLogResponse,
  WeeklySummary,
  MonthlySummary,
} from '../types/api.ts';
import { getToken, clearAuth, type User } from './auth.ts';
import { handleGateResponse } from './plan.ts';
import { isEmailUnverified, notifyEmailUnverified } from './verification.ts';
import i18n from '../i18n.ts';

// ---- Legacy API key helpers (kept for backward compat) ----

const STORAGE_KEY = 'cycling_api_key';

export function getApiKey(): string | null {
  // Prefer JWT token, fall back to legacy API key
  return getToken() || localStorage.getItem(STORAGE_KEY);
}

export function setApiKey(key: string): void {
  localStorage.setItem(STORAGE_KEY, key);
}

export function clearApiKey(): void {
  localStorage.removeItem(STORAGE_KEY);
  clearAuth();
}

// Global callback for 401 handling — App.tsx registers this
let onUnauthorized: (() => void) | null = null;

export function setOnUnauthorized(cb: () => void): void {
  onUnauthorized = cb;
}

// ---- Base URL ----

export const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8765';

// ---- Axios client ----

const client = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 120_000,
});

// Inject Authorization header on every request
client.interceptors.request.use((config) => {
  const token = getApiKey();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Both auth-revoking conditions go through the same cleanup so a re-enabled
// user's old JWT can't keep them stuck on a stale "logged in" UI.
function forceLogout(): void {
  clearAuth();
  onUnauthorized?.();
  globalThis.dispatchEvent(new CustomEvent('auth:logout'));
}

/** 403 from an AI route that would need Garmin rides the rider hasn't
 *  allowed the coach to use. */
export function isAiConsentRequired(status: number | undefined, body: unknown): boolean {
  return status === 403 && (body as { code?: string } | null)?.code === 'ai_consent_required';
}

// Backend returns 403 with this exact message when admin disables an account.
// Other 403s (e.g. "Admin key required") must NOT trigger logout.
const DISABLED_MESSAGE = 'Account disabled';

// Handle 401 / 403-disabled globally
client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const message = (error.response?.data as { message?: string } | undefined)?.message;
      if (status === 401 || (status === 403 && message === DISABLED_MESSAGE)) {
        forceLogout();
      } else if (handleGateResponse(status, error.response?.data) && message) {
        // Inline error text then reads "part of Soigneur Pro", not "status code 402".
        error.message = message;
      } else if (isEmailUnverified(status, error.response?.data)) {
        // Server-side verification gate: show the "Check your email" screen.
        notifyEmailUnverified();
      } else if (isAiConsentRequired(status, error.response?.data)) {
        // A Garmin ride without the rider's AI consent (API ai_consent_required).
        error.message = i18n.t('garmin.consentRequired');
      }
    }
    return Promise.reject(error);
  },
);

// Sign out: the server revokes every token issued to this user so far,
// then local auth is cleared. Best effort — a failed call still signs out
// locally.
export async function signOut(): Promise<void> {
  try {
    await client.post('/auth/logout', null, { timeout: 5_000 });
  } catch {
    // ignore: local sign-out still happens
  }
  clearAuth();
}

// ---- Authenticated fetch wrapper (for streaming / FormData) ----

export async function apiFetch(
  path: string,
  options: RequestInit = {},
): Promise<Response> {
  const token = getApiKey();
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> | undefined),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const resp = await fetch(`${API_BASE}${path}`, { ...options, headers });
  if (resp.status === 402 || resp.status === 429) {
    try {
      handleGateResponse(resp.status, await resp.clone().json());
    } catch {
      // Not JSON (e.g. a plain rate-limit page): leave it to the caller.
    }
  } else if (resp.status === 401) {
    forceLogout();
  } else if (resp.status === 403) {
    // Need to peek at the body to distinguish "Account disabled" from other
    // 403s. Clone first so the caller still gets an unread body.
    try {
      const body = await resp.clone().json() as { message?: string };
      if (body?.message === DISABLED_MESSAGE) {
        forceLogout();
      } else if (isEmailUnverified(resp.status, body)) {
        notifyEmailUnverified();
      }
    } catch {
      // 403 with a non-JSON body — leave caller to handle.
    }
  }
  return resp;
}

// ---- API methods ----

export const api = {
  // The signed-in user with their plan (Free / Pro, free messages left).
  me: () =>
    client.get<{ user: User }>('/auth/me').then(r => r.data.user),

  // The browser's IANA time zone; the server's days follow it.
  setTimezone: (timezone: string) =>
    client.put<{ timezone: string; changed: boolean }>('/auth/timezone', { timezone }).then(r => r.data),

  chat: (data: ChatRequest) =>
    client.post<ChatResponse>('/chat', data).then(r => r.data),

  zoneCalc: (data: ZoneCalcRequest) =>
    client.post<ZoneCalcResponse>('/zone-calculator', data).then(r => r.data),

  ftpTest: (data: FTPTestRequest) =>
    client.post<FTPTestResponse>('/ftp-test', data).then(r => r.data),

  trainingPlan: async (data: TrainingPlanRequest): Promise<TrainingPlanResponse> => {
    const fmt = data.format;
    if (fmt === 'pdf' || fmt === 'ical') {
      const resp = await client.post('/training-plan', data, { responseType: 'blob' });
      const ext = fmt === 'pdf' ? 'pdf' : 'ics';
      const mime = fmt === 'pdf' ? 'application/pdf' : 'text/calendar';
      const blob = new Blob([resp.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `training-plan.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return {};
    }
    return client.post<TrainingPlanResponse>('/training-plan', data).then(r => r.data);
  },

  recovery: (data: RecoveryRequest) =>
    client.post<RecoveryResponse>('/recovery-advice', data).then(r => r.data),

  racePrep: (data: RacePrepRequest) =>
    client.post<RacePrepResponse>('/race-prep', data).then(r => r.data),

  workoutLog: (data: WorkoutLogRequest) =>
    client.post<WorkoutLogResponse>('/workout-log', data).then(r => r.data),

  weekly: {
    // GET /weeks/{iso}/summary — live stats + flags + (cached) coach summary.
    get: (iso: string) =>
      client.get<WeeklySummary>(`/weeks/${iso}/summary`).then(r => r.data),
    // POST /weeks/{iso}/summary/generate — (re)generate the LLM summary
    // for the week and persist it. Rate-limited to 10/min server-side.
    generate: (iso: string) =>
      client.post<WeeklySummary>(`/weeks/${iso}/summary/generate`).then(r => r.data),
  },

  monthly: {
    // GET /months/{iso}/summary — live monthly stats + flags + (cached) summary.
    // {iso} must be a first-of-month ISO date e.g. 2026-05-01.
    get: (iso: string) =>
      client.get<MonthlySummary>(`/months/${iso}/summary`).then(r => r.data),
    // POST /months/{iso}/summary/generate — (re)generate the LLM summary.
    // Rate-limited to 10/min server-side.
    generate: (iso: string) =>
      client.post<MonthlySummary>(`/months/${iso}/summary/generate`).then(r => r.data),
  },
};

// ---- Typed fetch helpers for new routes ----

export { client as axiosClient };
